<div class="min-h-screen bg-cartco-mist text-slate-900 dark:bg-slate-900 dark:text-slate-100">
  <!-- Sidebar -->
  <aside
    id="vendor-menu"
    class="fixed inset-y-0 left-0 z-50 flex min-h-0 w-72 flex-col bg-white shadow-2xl shadow-slate-900/10 transition-transform dark:bg-slate-950"
    [ngClass]="{
      '-translate-x-full': !isSidebarOpen(),
      'translate-x-0': isSidebarOpen()
    }"
  >
    <div class="flex items-center gap-3 border-b border-slate-100 px-6 py-5 dark:border-slate-800">
      <!-- CartCo Logo -->
      <div class="flex items-center gap-2 shrink-0">
        <img src="/assets/i18n/logo.png" alt="CartCo Logo" class="h-20 w-auto object-contain">
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold tracking-wide text-slate-700 dark:text-slate-200 truncate">
          {{ vendor()?.name || 'Vendor Store' }}
        </p>
        <p class="text-xs text-slate-400 truncate">{{ userEmail() }}</p>
      </div>
    </div>

    <nav class="vendor-shell-nav-scroll min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-6">
      <ng-container *ngFor="let section of filteredNavSections(); trackBy: trackSection">
        <p class="px-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {{ section.title }}
        </p>
        <div class="mt-2 flex flex-col gap-1">
          <a
            *ngFor="let item of section.items; trackBy: trackNavItem"
            [routerLink]="item.route"
            routerLinkActive="bg-cartco-green/10 text-cartco-green dark:bg-slate-800"
            class="group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            (click)="onSidebarNavClick(item); closeMobileSidebar()"
          >
            <ion-icon [name]="item.icon" class="h-5 w-5"></ion-icon>
            <span class="flex-1">{{ item.label }}</span>
            <span *ngIf="item.badge" class="rounded-full bg-cartco-orange/10 px-2 text-xs font-semibold text-cartco-orange">
              {{ item.badge }}
            </span>
            <span *ngIf="item.route === '/tabs/orders' && pendingOrderCount() > 0" class="min-w-[20px] rounded-full bg-red-500 px-2 py-0.5 text-center text-xs font-bold text-white">
              {{ pendingOrderCount() }}
            </span>
          </a>
        </div>

        <div class="mb-6"></div>
      </ng-container>
    </nav>
  </aside>

  <!-- Main -->
  <div [ngClass]="{ 'lg:pl-72': isDesktopLayout() && isSidebarPinned() }">
    <!-- Tablet+: Full header with search and theme toggle -->
    <header class="vendor-shell-top-header sticky top-0 z-10 flex flex-nowrap items-center gap-2 border-b border-slate-200/60 bg-white/80 px-4 py-2 backdrop-blur dark:border-slate-800 dark:bg-slate-950/70 md:gap-3 md:px-5 md:py-3">
      <button
        *ngIf="showSidebarMenuButton()"
        type="button"
        class="vendor-shell-top-header__icon-btn shrink-0 rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-cartco-green hover:text-cartco-green dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        (click)="toggleSidebar()"
        aria-label="Open menu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" />
        </svg>
      </button>

      <div class="vendor-shell-top-header__actions ml-auto flex min-w-0 flex-1 items-center justify-end gap-2 md:max-w-none md:flex-none md:gap-3">
        <app-expandable-order-search
          *ngIf="showOrderSearch()"
          [value]="headerSearchQuery()"
          (valueChange)="onHeaderSearchInput($event)"
          (searchSubmit)="onHeaderSearchSubmit($event)"
          (cleared)="clearHeaderSearch()"
        />
        <button
          type="button"
          class="vendor-shell-top-header__icon-btn shrink-0 rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-cartco-green hover:text-cartco-green dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          (click)="toggleTheme()"
        >
        <ng-container *ngIf="isDark(); else sunIcon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
          </svg>
        </ng-container>
        <ng-template #sunIcon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
          </svg>
        </ng-template>
        </button>

        <div class="hidden items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2 text-left text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 md:flex">
        <div class="h-9 w-9 rounded-full bg-cartco-green/10 text-cartco-green ring-4 ring-cartco-green/20 dark:bg-cartco-green/20 dark:text-white dark:ring-cartco-green/10 flex items-center justify-center font-semibold">
          {{ userInitials() }}
        </div>
        <div class="leading-tight">
          <p class="truncate">{{ vendor()?.name || 'Vendor' }}</p>
          <p class="text-xs font-medium text-slate-400 dark:text-slate-500 truncate">{{ userEmail() }}</p>
        </div>
      </div>
      </div>
    </header>

    <main class="content-area min-h-[calc(100vh-4rem-env(safe-area-inset-top,0px))] bg-transparent pb-20">
      <div class="mx-auto max-w-7xl">
        <router-outlet />
      </div>
    </main>
    
    <!-- Footer -->
    <app-footer class="vendor-footer"></app-footer>
  </div>

  <!-- Mobile backdrop: after main content so it covers page headers + scroll area -->
  <div
    class="vendor-shell-mobile-backdrop lg:hidden"
    *ngIf="isMobileDrawerOpen()"
    (click)="closeMobileSidebar()"
    aria-hidden="true"
  ></div>
</div>

